import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { type Locale } from "@/shared/utils";
import { CALENDAR_LOCALE, Button } from "@/shared/ui";

type DayAbbreviation = "Mon" | "Tue" | "Wed" | "Thu" | "Fri" | "Sat" | "Sun";

const DAY_ABBREVIATIONS: DayAbbreviation[] = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

interface Props {
  label?: string;
  value?: DayAbbreviation[];
  onChange?: (days: DayAbbreviation[]) => void;
  locale: Locale;
  error?: string;
  disabled?: boolean;
}

export default function WorkingDays({
  label,
  value,
  onChange,
  locale,
  error,
  disabled = false,
}: Props) {
  const { t } = useTranslation("Common");
  const defaultDays = useMemo<DayAbbreviation[]>(() => ["Mon", "Tue", "Wed", "Thu", "Fri"], []);
  const [internalValue, setInternalValue] = useState<DayAbbreviation[]>(defaultDays);
  const [validationError, setValidationError] = useState<string>("");

  const currentValue = value ?? internalValue;
  const weekdays = CALENDAR_LOCALE[locale].weekdays;

  function handleDayToggle(dayIndex: number) {
    if (disabled) return;

    const dayAbbr = DAY_ABBREVIATIONS[dayIndex];
    const isSelected = currentValue.includes(dayAbbr);
    const newValue: DayAbbreviation[] = isSelected
      ? currentValue.filter((d) => d !== dayAbbr)
      : [...currentValue, dayAbbr];

    if (newValue.length === 0) {
      setValidationError(t("workingDays.validation.minDays"));
      return;
    }

    if (newValue.length > 6) {
      setValidationError(t("workingDays.validation.maxDays"));
      return;
    }

    setValidationError("");
    if (value === undefined) {
      setInternalValue(newValue);
    }
    onChange?.(newValue);
  }

  const displayError = error || validationError;

  return (
    <div className="flex flex-col gap-2">
      {label && (
        <label
          className={cn(
            "text-label-sm content-base-primary",
            disabled && "opacity-50",
          )}>
          {label}
        </label>
      )}

      <div className="flex gap-2">
        {weekdays.map((weekday, index) => {
          const dayAbbr = DAY_ABBREVIATIONS[index];
          const isSelected = currentValue.includes(dayAbbr);

          return (
            <Button
              key={dayAbbr}
              type="button"
              variant={isSelected ? "primary" : "secondary"}
              size="md"
              disabled={disabled}
              onClick={() => handleDayToggle(index)}
              className={cn(
                "w-10 h-10 p-0 flex items-center justify-center",
                !isSelected && "opacity-50",
              )}>
              {weekday}
            </Button>
          );
        })}
      </div>

      {displayError && !disabled && (
        <p className="text-body-regular-xs text-red-500">{displayError}</p>
      )}
    </div>
  );
}


















